import React from 'react'

type SvgObjectProps = {
  src: string | null | undefined
  alt?: string
  width?: number | string
  height?: number | string
  style?: React.CSSProperties
  className?: string
  objectProps?: Omit<
    React.ObjectHTMLAttributes<HTMLObjectElement>,
    'data' | 'type' | 'children' | 'style' | 'className'
  >
  fallbackSrc?: string
  imgProps?: Omit<React.ImgHTMLAttributes<HTMLImageElement>, 'src' | 'alt' | 'style' | 'className'>
}

function isLikelySvg(url: string) {
  // 쿼리스트링/해시 고려
  const clean = url.split('?')[0].split('#')[0].toLowerCase()
  return clean.endsWith('.svg') || clean.includes('image/svg') // 혹시 data url이나 mime 힌트가 있을 때
}

export function SvgObject({
  src,
  alt = '',
  width,
  height,
  style,
  className,
  objectProps,
  fallbackSrc,
  imgProps,
}: SvgObjectProps) {
  const sizeStyle: React.CSSProperties = {
    width,
    height,
    pointerEvents: 'none',
    ...style,
  }

  // src가 null이나 undefined인 경우 빈 div 반환
  if (!src) {
    return <div className={className} style={sizeStyle} aria-label={alt || undefined} />
  }

  const resolvedFallback = fallbackSrc ?? src

  // ✅ PNG/JPG/WebP 등은 object 쓰지 말고 img로 바로 렌더
  if (!isLikelySvg(src)) {
    return (
      // eslint-disable-next-line @next/next/no-img-element
      <img
        src={src}
        alt={alt}
        className={className}
        style={sizeStyle}
        {...imgProps}
        onError={(e) => {
          // 기존 onError도 살리면서 fallback 적용
          imgProps?.onError?.(e)
          if (resolvedFallback && (e.currentTarget as HTMLImageElement).src !== resolvedFallback) {
            (e.currentTarget as HTMLImageElement).src = resolvedFallback
          }
        }}
      />
    )
  }

  // ✅ SVG만 object 사용
  return (
    <object
      data={src}
      type="image/svg+xml"
      className={className}
      style={sizeStyle}
      aria-label={alt || undefined}
      {...objectProps}
    >
      {/* eslint-disable-next-line @next/next/no-img-element */}
      <img
        src={resolvedFallback}
        alt={alt}
        className={className}
        style={sizeStyle}
        {...imgProps}
      />
    </object>
  )
}
